// OAE domain logic — frequency lists, SNR + Pass/Refer computation,
// bilateral summary, and helpers to hydrate/mutate the OAE state
// stored on the test session. Kept dependency-free so both the live
// editor (OAEPanel) and the print modal (OAEReportModal) can share
// exactly the same math.

export const TEOAE_FREQS = [1000, 1500, 2000, 3000, 4000, 6000];       // Hz
export const DPOAE_FREQS = [500, 1000, 1500, 2000, 3000, 4000, 6000, 8000]; // Hz
export const OAE_PASS_SNR_DB = 6;
export const OAE_Y_MIN = -20;
export const OAE_Y_MAX = 30;

export const ENV_OPTIONS = ['Sound Booth', 'Quiet Room'];
export const TEST_TYPES = ['TEOAE', 'DPOAE'];
export const PURPOSES = ['Screening', 'Diagnostic'];

export const DEFAULT_INTERPRETATION =
  'OAEs are considered PRESENT (Pass) at a frequency when the SNR is at ' +
  'least 6 dB. A PASS suggests intact outer hair cell function. A REFER ' +
  'indicates the response was below noise floor and warrants further ' +
  'audiologic evaluation. Absence of OAEs does not solely diagnose hearing ' +
  'loss and must be interpreted alongside other test findings.';

export const freqsFor = (testType) =>
  testType === 'TEOAE' ? TEOAE_FREQS : DPOAE_FREQS;

export const fmtFreq = (hz) => (hz % 1000 === 0 ? `${hz / 1000}` : `${hz / 1000}`);

// Return { oae, noise, snr, result } for a given ear/freq
export function pointAt(ear, freqHz) {
  const p = ear?.points?.[String(freqHz)] || {};
  const oae = typeof p.oae === 'number' ? p.oae : null;
  const noise = typeof p.noise === 'number' ? p.noise : null;
  const snr = (oae !== null && noise !== null) ? +(oae - noise).toFixed(1) : null;
  const result = snr === null ? null : (snr >= OAE_PASS_SNR_DB ? 'Pass' : 'Refer');
  return { oae, noise, snr, result };
}

// Overall ear: Pass if majority of frequencies (with entered data) Pass.
export function overallResult(ear, freqs) {
  let pass = 0, refer = 0;
  freqs.forEach((f) => {
    const { result } = pointAt(ear, f);
    if (result === 'Pass') pass += 1;
    else if (result === 'Refer') refer += 1;
  });
  const total = pass + refer;
  if (!total) return null;                       // no data yet
  return pass > refer ? 'Pass' : (pass < refer ? 'Refer' : 'Refer');
}

export function bilateralSummary(right, left) {
  if (!right && !left) return null;
  if (right === 'Pass' && left === 'Pass') return 'Bilateral Pass';
  if (right === 'Refer' && left === 'Refer') return 'Bilateral Refer';
  if (right === 'Pass' && left === 'Refer') return 'Right Pass · Left Refer';
  if (right === 'Refer' && left === 'Pass') return 'Right Refer · Left Pass';
  // Partial (one ear no data)
  if (right && !left) return `Right ${right} · Left not tested`;
  if (left && !right) return `Left ${left} · Right not tested`;
  return null;
}

// Ensure the OAE object has the expected shape; returns a NEW object.
export function normaliseOAE(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    test_type: TEST_TYPES.includes(d.test_type) ? d.test_type : 'DPOAE',
    purpose:   PURPOSES.includes(d.purpose)     ? d.purpose   : 'Screening',
    right: {
      points: (d.right && d.right.points) ? { ...d.right.points } : {},
    },
    left: {
      points: (d.left && d.left.points) ? { ...d.left.points } : {},
    },
    probe_type:     d.probe_type     || '',
    stimulus:       d.stimulus       || '',
    equipment:      d.equipment      || '',
    environment:    ENV_OPTIONS.includes(d.environment) ? d.environment : 'Sound Booth',
    comments:       d.comments       || '',
    interpretation: d.interpretation || DEFAULT_INTERPRETATION,
    recommendations: d.recommendations || '',
    // Retain legacy `fields` bag if callers still write to it — the
    // widened backend schema keeps everything intact, and the report
    // renderer prefers the rich fields above.
    fields: d.fields || {},
  };
}

// Immutable setter helpers — return a new OAE state
export function setEarPoint(oae, ear, freqHz, kind, value) {
  const clone = { ...oae, [ear]: { points: { ...(oae[ear]?.points || {}) } } };
  const key = String(freqHz);
  const existing = clone[ear].points[key] || {};
  const next = { ...existing };
  if (value === null || value === undefined || value === '') delete next[kind];
  else next[kind] = Number(value);
  if (Object.keys(next).length === 0) delete clone[ear].points[key];
  else clone[ear].points[key] = next;
  return clone;
}

export function clearEar(oae, ear) {
  return { ...oae, [ear]: { points: {} } };
}
